import { capabilitiesFromContext } from "@/services/price-channel/feature-flags";

let activeAdapter = null;
async function sessionAdapter() {
  if (!activeAdapter) {
    // request 模块本身读取 Vuex Token；延迟加载避免 Store 初始化时形成循环依赖。
    const { getPriceChannelClient } = await import("@/services/price-channel/client");
    activeAdapter = getPriceChannelClient();
  }
  return activeAdapter;
}
const createState = () => ({
  context: null,
  release: null,
  loading: false,
  error: null,
  requestId: 0,
});

/**
 * 正式价格频道会话只保存轻量身份和 Release，不常驻大批时序数据。
 * 权限从 Spring context 消费；这里的展示能力不替代服务端逐次鉴权。
 */
export default {
  namespaced: true,
  state: createState,
  getters: {
    currentIdentity(state) {
      const identities = state.context?.identities || [];
      return identities.find(
        (item) => (item.userIdentityId || item.id) === state.context.activeUserIdentityId,
      ) || null;
    },
    actions: (state) => state.context?.actions || [],
    capabilities: (state) => capabilitiesFromContext(state.context),
    releaseVersion: (state) => state.release?.releaseVersion || null,
  },
  mutations: {
    START(state) {
      state.requestId += 1;
      state.loading = true;
      state.error = null;
    },
    SET_CONTEXT(state, context) {
      state.context = context;
    },
    SET_RELEASE(state, release) {
      state.release = release;
    },
    FINISH(state) {
      state.loading = false;
    },
    SET_ERROR(state, error) {
      state.error = error;
      state.loading = false;
    },
    CLEAR(state) {
      Object.assign(state, createState(), { requestId: state.requestId + 1 });
    },
  },
  actions: {
    async initializePublic({ commit, state }) {
      commit("START");
      const requestId = state.requestId;
      try {
        const adapter = await sessionAdapter();
        if (state.requestId !== requestId) return null;
        const release = await adapter.loadActiveRelease();
        if (state.requestId !== requestId) return null;
        commit("SET_RELEASE", release);
        commit("FINISH");
        return release;
      } catch (error) {
        if (state.requestId === requestId) commit("SET_ERROR", error);
        throw error;
      }
    },
    async initializeBusiness({ commit, state }) {
      commit("START");
      // 重新核验期间撤下旧权限，避免接口失败后继续展示过期身份菜单。
      commit("SET_CONTEXT", null);
      const requestId = state.requestId;
      try {
        const adapter = await sessionAdapter();
        if (state.requestId !== requestId) return null;
        const context = await adapter.loadContext();
        if (state.requestId !== requestId) return null;
        commit("SET_CONTEXT", context);
        commit("FINISH");
        // 会员身份来自 Spring/MySQL，不依赖 Platform 是否已有公开 Release。
        return { context, release: state.release };
      } catch (error) {
        if (state.requestId === requestId) commit("SET_ERROR", error);
        throw error;
      }
    },
    initialize({ dispatch }) {
      return dispatch("initializeBusiness");
    },
    async switchIdentity({ commit, state }, userIdentityId) {
      commit("START");
      // 切换请求发出前立即撤下旧主体的权限与公开 Release。
      commit("SET_CONTEXT", null);
      commit("SET_RELEASE", null);
      const requestId = state.requestId;
      try {
        const adapter = await sessionAdapter();
        if (state.requestId !== requestId) return null;
        adapter.invalidateCurrentSelection();
        const context = await adapter.switchIdentity(userIdentityId);
        if (state.requestId !== requestId) return null;
        commit("SET_CONTEXT", context);
        // 公开行情在需要时重新取 Release；身份切换不能被 Platform 不可用阻断。
        commit("FINISH");
        return { context, release: null };
      } catch (error) {
        if (state.requestId === requestId) commit("SET_ERROR", error);
        throw error;
      }
    },
    clear({ commit }) {
      if (activeAdapter) {
        activeAdapter.resetRelease();
        activeAdapter.setVisibilityPartition("anonymous");
      }
      commit("CLEAR");
    },
  },
};
